/**
 * i18n — one language store for the site and every terminal window.
 *
 * English source text IS the key:  tr('Place order')  →  'Выставить ордер'.
 * Russian lives in ./i18n.ru.json; a missing entry falls back to the English
 * text, so an untranslated string is never blank. `{name}` placeholders are
 * filled from the second argument.
 *
 * The choice persists in localStorage `qr_lang` (same key the site always
 * used). Terminal windows share one origin, so the `storage` event keeps
 * every open window in sync.
 */
import { useSyncExternalStore } from 'react';
import ruDict from './i18n.ru.json';

const RU: Record<string, string> = ruDict;

export type Lang = 'en' | 'ru';
const KEY = 'qr_lang';
const listeners = new Set<() => void>();

function read(): Lang | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'ru' || v === 'en' ? v : null;
  } catch { return null; }
}

let lang: Lang = read() ?? 'en';

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    const v = read();
    if (v && v !== lang) { lang = v; listeners.forEach((l) => l()); }
  });
}

export function getLang(): Lang { return lang; }
/** True when nobody has picked a language yet (first launch). */
export function langIsUnset(): boolean { return read() === null; }

export function setLang(next: Lang): void {
  if (next !== 'en' && next !== 'ru') return;
  try { localStorage.setItem(KEY, next); } catch { /* private mode */ }
  if (next === lang) return;
  lang = next;
  if (typeof document !== 'undefined') document.documentElement.lang = next;
  listeners.forEach((l) => l());
}

// Templates ("trading on {venue} is not supported yet") as regexes, so a
// message that arrives already filled in (Rust / server errors) still maps.
let patterns: [RegExp, string, string[]][] | null = null;
function byPattern(msg: string): string | undefined {
  patterns ??= Object.keys(RU).filter((k) => k.includes('{')).map((k) => {
    const names: string[] = [];
    const rx = k.split(/\{(\w+)\}/).map((part, i) => {
      if (i % 2) { names.push(part); return '(.+?)'; }
      return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }).join('');
    return [new RegExp(`^${rx}$`, 's'), RU[k], names];
  });
  for (const [rx, ru, names] of patterns) {
    const m = rx.exec(msg);
    if (m) return names.reduce((out, n, i) => out.split(`{${n}}`).join(m[i + 1]), ru);
  }
  return undefined;
}

export function tr(en: string, vars?: Record<string, string | number>): string {
  let s = lang === 'ru' ? (RU[en] ?? (vars ? undefined : byPattern(en)) ?? en) : en;
  if (vars) for (const k in vars) s = s.split(`{${k}}`).join(String(vars[k]));
  return s;
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** Re-renders the caller on language change. */
export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang, getLang);
}

/** For data that already carries both texts (settings defs with labelRu). */
export function pick(en: string, ru?: string): string {
  return lang === 'ru' ? (ru || tr(en)) : en;
}

/** Free text built from fixed sentences (server-generated situation
 * summaries): whole-string template first, else sentence by sentence. */
export function trText(text: string | null | undefined): string {
  if (!text || lang !== 'ru') return text ?? '';
  const whole = tr(text);
  if (whole !== text) return whole;
  return text.split(/(?<=[.!?])\s+(?=[A-Z0-9$(])/).map((s) => tr(s)).join(' ');
}
